import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import LoggingAPI from "@/api/logging";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { ApiKey } from "../api-keys.types";
import { useDeleteApiKey } from "../api-keys.query";

export const DeleteApiKeyModal: React.FC<{
  show: boolean;
  onClose?: () => void;
  onSubmit?: (data: ApiKey) => void;
  onCancel?: () => void;
  defaultFormData: ApiKey;
  buttonProps?: {
    submitText?: string;
    cancelText?: string;
  };
}> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps = {},
  defaultFormData,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { currentUser } = usePermissions();

  const mutation = useDeleteApiKey();
  const { mutate: deleteWebhook } = mutation;

  const handleSubmit = async () => {
    setSubmitting(true);
    deleteWebhook(defaultFormData.uuid);
    onSubmit?.(defaultFormData);
    setSubmitting(false);

    await LoggingAPI.createLog({
      case_id: undefined,
      user_id: currentUser.user_id,
      message: `Deleted API key: ${defaultFormData.uuid}`,
      event: "api-key:delete",
      object_type: "api-key",
      object_uuid: defaultFormData.uuid,
    });
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Delete API Key</Modal.Title>
      <div style={{ fontWeight: 600, margin: "20px 0px" }}>
        Are you sure you want to delete this API key{" "}
        <span style={{ fontWeight: 600 }}>"{defaultFormData?.uuid}"</span>?
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Delete API Key"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
